// /app/revision — start a Revision (BRIEFS C8.1, SPEC §5 item 7, D-27).
// Chapter multi-select (released chapters; "Select all"), one big Start button; under the
// list, how many True/False lines and questions the chosen chapters have (C11.10). The session
// itself is /app/revision/:id (AppRevisionSession.js). An open revision can be continued.
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOpenRevision, getRevisionChapters, startRevision } from '../../api/v2';
import { track, useActivityArea } from '../../components/app/activity';
import { IconArrowRight } from '../../components/app/icons';
import { errorText, plural } from '../../components/app/testParts';
import { C, FONT, button } from '../../components/app/theme';
import { CheckRow, chapterName } from './AppPractice';
import { Empty, LoadError, Loading } from '../../components/app/LoadState';

const box = {
  boxSizing: 'border-box', padding: 20, background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 12,
};

export default function AppRevision() {
  useActivityArea('revision');
  const navigate = useNavigate();
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const chaptersQ = useQuery({ queryKey: ['v2', 'revision-chapters'], queryFn: getRevisionChapters });
  const openQ = useQuery({ queryKey: ['v2', 'open-revision'], queryFn: getOpenRevision });
  const chapters = useMemo(() => (chaptersQ.data && chaptersQ.data.chapters) || [], [chaptersQ.data]);
  const open = openQ.data && openQ.data.session;
  const usable = chapters.filter((c) => (c.questions || 0) + (c.one_liners || 0) > 0);
  const allPicked = usable.length > 0 && usable.every((c) => picked.includes(c.chapter_id));
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const canStart = picked.length > 0 && !busy;
  const chosen = chapters.filter((c) => picked.includes(c.chapter_id));
  const chosenLines = chosen.reduce((n, c) => n + (c.one_liners || 0), 0);
  const chosenQuestions = chosen.reduce((n, c) => n + (c.questions || 0), 0);

  async function start() {
    if (!canStart) return;
    setBusy(true);
    setError('');
    try {
      const res = await startRevision(picked);
      track('rev_start', { test_id: res.session_id, details: { chapter_ids: picked } });
      navigate(`/app/revision/${res.session_id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div data-testid="v2-revision-start" style={{ maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 28, fontWeight: 600 }}>Revision</h1>
        <div style={{ fontSize: 15, color: C.muted }}>
          True or False lines and questions, mixed: 4 lines, then 1 question. Each one is checked at once. Keep going until you press Stop.
        </div>
      </div>

      {open && (
        <div data-testid="v2-open-revision" style={{
          boxSizing: 'border-box', padding: '14px 18px', borderRadius: 12, background: C.tint,
          border: '1px solid #C7D2FE', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontFamily: FONT.heading, fontSize: 15, fontWeight: 600, color: C.primaryDark }}>Your revision is waiting for you</div>
            <div style={{ fontSize: 14, color: C.muted }}>
              {open.counts.done} done · {open.counts.right} right · {open.counts.wrong} wrong
            </div>
            <div style={{ fontSize: 13, color: C.muted }}>If you start a new revision, this one is stopped first.</div>
          </div>
          <Link to={`/app/revision/${open.session_id}`} data-testid="v2-resume-revision" style={{
            ...button.primary, display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box',
          }}>Continue<IconArrowRight /></Link>
        </div>
      )}

      <div style={box}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <h2 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 17, fontWeight: 600 }}>Choose chapters</h2>
          {usable.length > 1 && (
            <button type="button" data-testid="v2-pick-all" onClick={() => setPicked(allPicked ? [] : usable.map((c) => c.chapter_id))}
              style={{ ...button.secondary, minHeight: 44, fontSize: 14 }}>
              {allPicked ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>
        {chaptersQ.isLoading && <Loading text="Loading chapters…" />}
        {chaptersQ.isError && <LoadError onRetry={chaptersQ.refetch} />}
        {!chaptersQ.isLoading && !chaptersQ.isError && chapters.length === 0 && (
          <Empty text="No chapters are open yet." />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {chapters.map((ch) => {
            const empty = (ch.questions || 0) + (ch.one_liners || 0) === 0;
            return (
              <CheckRow key={ch.chapter_id} checked={picked.includes(ch.chapter_id)} disabled={empty}
                onClick={() => toggle(ch.chapter_id)} testId={`v2-pick-ch-${ch.chapter_id}`}>
                <span style={{ flexGrow: 1 }}>{chapterName(ch)}</span>
                <span style={{ fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>
                  {plural(ch.one_liners || 0, 'line', 'lines')} · {plural(ch.questions || 0, 'question', 'questions')}
                </span>
              </CheckRow>
            );
          })}
        </div>
        {chosen.length > 0 && (
          <div data-testid="v2-rev-picked-counts" style={{
            boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, background: C.tint,
            fontSize: 15, color: C.primaryDark, fontWeight: 500,
          }}>
            You chose {plural(chosen.length, 'chapter', 'chapters')}: {plural(chosenLines, 'True/False line', 'True/False lines')} · {plural(chosenQuestions, 'question', 'questions')}
          </div>
        )}
      </div>

      {error && <div data-testid="v2-start-error" style={{ color: C.bad, fontSize: 15 }}>{error}</div>}
      <div>
        <button type="button" data-testid="v2-rev-start" onClick={start} disabled={!canStart}
          style={{ ...button.primary, minHeight: 56, padding: '0 40px', fontSize: 18, opacity: canStart ? 1 : 0.5, cursor: canStart ? 'pointer' : 'default' }}>
          {busy ? 'Starting…' : 'Start'}
        </button>
        {picked.length === 0 && <span style={{ marginLeft: 12, fontSize: 14, color: C.muted }}>Choose at least one chapter.</span>}
      </div>
    </div>
  );
}
